import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Switch from "@mui/material/Switch";
import DropDownListCustomized from "../../ui/kit-mdtw/DropDownListCustomized";
import { IOptionDropDownList } from "../../../interfaces/IDropDownList";
import { useEscapeKey } from "../../../hooks/useEscapeKey";
import { getUserErrorMessage } from "./postulacionesUtils";
import {
  AlcanceCompletitud,
  getConfiguracionCompletitud,
  getOpcionesAlcance,
  guardarConfiguracionCompletitud,
  ICampoCompletitud,
  IConfiguracionCompletitud,
  IOpcionAlcance,
  ModoCompletitud,
  OrigenModo,
} from "../../../services/configuracionCompletitudService";

/**
 * Configuración de qué datos y documentos se validan para la completitud.
 *
 * Arriba se elige QUÉ se configura, con dos opciones explícitas:
 * - "Configuración base": se aplica por defecto a todos los clientes y a las
 *   personas sin un proceso asignado.
 * - "Cliente o área de negocio": se elige un cliente y/o un área (el área se
 *   filtra por el cliente) y se guarda solo lo que cambia respecto de la base
 *   (o de su cliente, para un área); el resto se hereda.
 * - Cada dato es un switch: encendido = se valida, apagado = no importa.
 *
 * El nivel EMPRESA sigue existiendo en el backend, pero por ahora no se ofrece.
 */

export const ORIGEN_LABEL: Record<OrigenModo, string> = {
  BASE: "la base",
  EMPRESA: "la empresa",
  CLIENTE: "el cliente",
  AREA_NEGOCIO: "el área",
  DEFECTO: "la base",
};

/**
 * Dos secciones: todos los datos juntos y los documentos aparte. Dentro de los
 * datos se conserva el orden del catálogo (identidad, contacto, formulario).
 */
export const agruparCampos = (fields: ICampoCompletitud[]) => [
  {
    key: "DATOS",
    title: "Datos de la postulación",
    help: "Los datos que pide el formulario (estado civil, salud, AFP, banco, tallas, turno…) se validan solo si el formulario de la postulación los pide. Enfermedad y discapacidad exigen la observación solo si la persona respondió \"Sí\".",
    fields: fields.filter((field) => field.group !== "DOCUMENTOS"),
  },
  {
    key: "DOCUMENTOS",
    title: "Documentos",
    help: "Se validan solo si el formulario de la postulación pide subirlos.",
    fields: fields.filter((field) => field.group === "DOCUMENTOS"),
  },
].filter((section) => section.fields.length > 0);

type ModoPantalla = "BASE" | "ESPECIFICA";

/** Lo que guarda el switch encendido (con backend anterior, se deduce de los modos). */
export const modoValidado = (field: ICampoCompletitud): ModoCompletitud =>
  field.validatedMode ?? (field.modes.includes("SEGUN_FORMULARIO") ? "SEGUN_FORMULARIO" : "OBLIGATORIO");

/** Modo que vale hoy para el campo: el propio del nivel o, si hereda, el heredado. */
export const modoEfectivo = (
  config: IConfiguracionCompletitud,
  draft: Record<string, ModoCompletitud>,
  key: string,
): ModoCompletitud => {
  const own = draft[key] ?? "HEREDA";
  return own === "HEREDA" ? config.inherited[key]?.mode ?? "NO_APLICA" : own;
};

export const seValida = (
  config: IConfiguracionCompletitud,
  draft: Record<string, ModoCompletitud>,
  key: string,
): boolean => modoEfectivo(config, draft, key) !== "NO_APLICA";

/**
 * Nuevo borrador al mover el switch. En un cliente o área, volver al mismo
 * valor que se hereda borra la regla propia (HEREDA), así un cambio posterior
 * en la base le sigue llegando. La base guarda siempre su valor explícito.
 */
export const alternarCampo = (
  config: IConfiguracionCompletitud,
  draft: Record<string, ModoCompletitud>,
  field: ICampoCompletitud,
  validar: boolean,
): Record<string, ModoCompletitud> => {
  const inheritedOn = (config.inherited[field.key]?.mode ?? "NO_APLICA") !== "NO_APLICA";
  const mode: ModoCompletitud = config.scope !== "BASE" && validar === inheritedOn
    ? "HEREDA"
    : validar ? modoValidado(field) : "NO_APLICA";
  return { ...draft, [field.key]: mode };
};

/** Solo lo que cambió respecto de lo guardado, para no tocar fechas de reglas iguales. */
export const cambiosPendientes = (
  config: IConfiguracionCompletitud,
  draft: Record<string, ModoCompletitud>,
): Record<string, ModoCompletitud> => {
  const changes: Record<string, ModoCompletitud> = {};
  for (const field of config.fields) {
    const saved = config.own[field.key] ?? "HEREDA";
    const current = draft[field.key] ?? "HEREDA";
    if (saved !== current) changes[field.key] = current;
  }
  return changes;
};

/** Nivel que se edita según lo elegido: el área gana sobre el cliente; sin nada, la base. */
export const resolverAlcance = (clientId: string, areaId: string): { scope: AlcanceCompletitud; scopeId: string } => {
  if (areaId) return { scope: "AREA_NEGOCIO", scopeId: areaId };
  if (clientId) return { scope: "CLIENTE", scopeId: clientId };
  return { scope: "BASE", scopeId: "" };
};

/** Áreas del cliente elegido; sin cliente, todas (con el cliente entre paréntesis). */
export const opcionesArea = (options: IOpcionAlcance[], clientId: string): IOptionDropDownList[] => {
  const clients = new Map(options.filter((o) => o.scope === "CLIENTE").map((o) => [o.id, o.name]));
  return options
    .filter((o) => o.scope === "AREA_NEGOCIO" && (!clientId || o.parentId === clientId))
    .map((o) => ({
      code: o.id,
      name: clientId || !o.parentId ? o.name : `${o.name} (${clients.get(o.parentId) ?? o.parentId})`,
    }));
};

const conReglas = (o: IOpcionAlcance): string =>
  o.rules > 0 ? ` · ${o.rules} regla${o.rules === 1 ? "" : "s"}` : "";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved?: (message: string) => void;
}

interface ISelectorValues {
  cliente: { value?: IOptionDropDownList; valid: boolean };
  area: { value?: IOptionDropDownList; valid: boolean };
}

const EMPTY_SELECTOR: ISelectorValues = { cliente: { valid: true }, area: { valid: true } };

const ConfiguracionCompletitudModal = ({ open, onClose, onSaved }: Props) => {
  const [modoPantalla, setModoPantalla] = useState<ModoPantalla>("BASE");
  const [options, setOptions] = useState<IOpcionAlcance[]>([]);
  const [selector, setSelector] = useState<ISelectorValues>(EMPTY_SELECTOR);
  // Cambia para volver a montar los selectores (DropDownListCustomized no se
  // limpia desde afuera): al cambiar de cliente, el área queda en blanco.
  const [selectorKey, setSelectorKey] = useState<number>(0);
  const [config, setConfig] = useState<IConfiguracionCompletitud | null>(null);
  const [draft, setDraft] = useState<Record<string, ModoCompletitud>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeKey(onClose, open);

  const clientId = selector.cliente.value?.code ?? "";
  const areaId = selector.area.value?.code ?? "";
  const { scope, scopeId } = modoPantalla === "BASE"
    ? { scope: "BASE" as AlcanceCompletitud, scopeId: "" }
    : resolverAlcance(clientId, areaId);
  // En "Cliente o área" sin nada elegido todavía no hay qué mostrar.
  const awaitingSelection = modoPantalla === "ESPECIFICA" && scope === "BASE";

  useEffect(() => {
    if (!open) return;
    setModoPantalla("BASE");
    setSelector(EMPTY_SELECTOR);
    setSelectorKey((current) => current + 1);
    getOpcionesAlcance()
      .then(setOptions)
      .catch((err) => setError(getUserErrorMessage(err, "No se pudieron cargar los clientes y áreas.")));
  }, [open]);

  const loadConfig = useCallback(async (target: AlcanceCompletitud, targetId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getConfiguracionCompletitud(target, targetId);
      setConfig(data);
      setDraft({ ...data.own });
    } catch (err) {
      setConfig(null);
      setError(getUserErrorMessage(err, "No se pudo cargar la configuración."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (awaitingSelection) {
      setConfig(null);
      setDraft({});
      return;
    }
    void loadConfig(scope, scopeId);
  }, [open, scope, scopeId, awaitingSelection, loadConfig]);

  const clientOptions: IOptionDropDownList[] = useMemo(
    () => options.filter((o) => o.scope === "CLIENTE").map((o) => ({ code: o.id, name: `${o.name}${conReglas(o)}` })),
    [options],
  );
  const areaOptions = useMemo(() => opcionesArea(options, clientId), [options, clientId]);

  const changes = config ? cambiosPendientes(config, draft) : {};
  const hasChanges = Object.keys(changes).length > 0;

  const confirmDiscard = (): boolean =>
    !hasChanges || window.confirm("Hay cambios sin guardar en esta configuración. ¿Descartarlos?");

  const handleSelectorChange = (next: ISelectorValues) => {
    if (!confirmDiscard()) {
      // Vuelve a montar los selectores con lo que había.
      setSelectorKey((current) => current + 1);
      return;
    }
    const nextClient = next.cliente.value?.code ?? "";
    const nextArea = next.area.value;
    const areaParent = nextArea ? options.find((o) => o.scope === "AREA_NEGOCIO" && o.id === nextArea.code)?.parentId : null;

    if (nextClient !== clientId) {
      // Cambió el cliente: un área de otro cliente deja de valer.
      const keepArea = nextArea && areaParent === nextClient;
      setSelector({ cliente: next.cliente, area: keepArea ? next.area : { valid: true } });
      setSelectorKey((current) => current + 1);
      return;
    }
    if (nextArea && !nextClient && areaParent) {
      // Área elegida sin cliente: se completa su cliente para que se vea de quién es.
      const parent = clientOptions.find((o) => o.code === areaParent);
      setSelector({ cliente: { value: parent, valid: true }, area: next.area });
      setSelectorKey((current) => current + 1);
      return;
    }
    setSelector(next);
  };

  const changeModoPantalla = (next: ModoPantalla) => {
    if (next === modoPantalla || !confirmDiscard()) return;
    setModoPantalla(next);
    setSelector(EMPTY_SELECTOR);
    setSelectorKey((current) => current + 1);
  };

  const handleSave = async () => {
    if (!config || !hasChanges || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await guardarConfiguracionCompletitud(config.scope, config.scopeId, changes);
      setConfig(result);
      setDraft({ ...result.own });
      setOptions(await getOpcionesAlcance().catch(() => options));
      onSaved?.(result.message);
    } catch (err) {
      setError(getUserErrorMessage(err, "No se pudo guardar la configuración."));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (confirmDiscard()) onClose();
  };

  const sections = useMemo(() => (config ? agruparCampos(config.fields) : []), [config]);

  const isBase = scope === "BASE";
  const clientName = selector.cliente.value?.name.replace(/ · \d+ reglas?$/, "") ?? "";
  const targetName = isBase
    ? "Configuración base"
    : scope === "AREA_NEGOCIO"
      ? `Área de negocio ${(selector.area.value?.name ?? "").replace(/ \(.*\)$/, "")}${clientName ? ` · ${clientName}` : ""}`
      : `Cliente ${clientName}`;
  const inheritsFrom = scope === "AREA_NEGOCIO" ? "su cliente o, si no tiene, de la base" : "la base";
  // Colores por modo: azul = base, violeta = un cliente o área en particular.
  const theme = isBase
    ? { accent: "#1d4ed8", soft: "#eff6ff", border: "#bfdbfe", text: "#1e3a8a" }
    : { accent: "#6d28d9", soft: "#f5f3ff", border: "#ddd6fe", text: "#4c1d95" };

  const font = "Roboto, sans-serif";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Configurar completitud"
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 16, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            style={{ width: "min(760px, 100%)", maxHeight: "90vh", display: "flex", flexDirection: "column", background: "#fff", borderRadius: "14px", overflow: "hidden", boxShadow: "0 20px 50px rgba(15,23,42,0.3)", fontFamily: font }}
          >
            <div style={{ padding: "18px 22px", background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: "16px", fontWeight: 700 }}>Configurar completitud</div>
                <div style={{ fontSize: "12.5px", opacity: 0.8, marginTop: "2px" }}>
                  Qué datos y documentos se validan para el porcentaje de completitud.
                </div>
              </div>
              <button type="button" onClick={handleClose} aria-label="Cerrar" style={{ background: "transparent", border: 0, color: "#fff", cursor: "pointer" }}>
                <CloseIcon style={{ fontSize: 20 }} />
              </button>
            </div>

            {/* position + zIndex: la lista de los selectores se despliega sobre los switches. */}
            <div style={{ position: "relative", zIndex: 2, padding: "14px 22px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>
                ¿Qué quieres configurar?
              </div>
              <div role="radiogroup" aria-label="Qué configurar" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>
                {([
                  {
                    value: "BASE" as ModoPantalla,
                    title: "Configuración base",
                    detail: "Para todos los clientes y para quienes no tienen un proceso asignado.",
                    color: "#1d4ed8",
                    soft: "#eff6ff",
                  },
                  {
                    value: "ESPECIFICA" as ModoPantalla,
                    title: "Un cliente o área de negocio",
                    detail: "Solo lo que cambia para ese cliente o área; lo demás sigue la base.",
                    color: "#6d28d9",
                    soft: "#f5f3ff",
                  },
                ]).map((option) => {
                  const selected = modoPantalla === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => changeModoPantalla(option.value)}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        padding: "10px 12px",
                        borderRadius: "10px",
                        border: `${selected ? 2 : 1}px solid ${selected ? option.color : "#cbd5e1"}`,
                        background: selected ? option.soft : "#fff",
                        textAlign: "left",
                        cursor: "pointer",
                        fontFamily: font,
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: "16px",
                          height: "16px",
                          marginTop: "1px",
                          flexShrink: 0,
                          borderRadius: "50%",
                          border: `2px solid ${selected ? option.color : "#94a3b8"}`,
                          boxShadow: selected ? `inset 0 0 0 3px #fff, inset 0 0 0 8px ${option.color}` : "none",
                        }}
                      />
                      <span>
                        <span style={{ display: "block", fontSize: "13px", fontWeight: 700, color: selected ? option.color : "#334155" }}>{option.title}</span>
                        <span style={{ display: "block", fontSize: "11.5px", color: "#64748b", marginTop: "2px", lineHeight: 1.35 }}>{option.detail}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {modoPantalla === "ESPECIFICA" && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px", marginTop: "12px" }}>
                  <div>
                    <div style={{ fontSize: "12px", fontWeight: 600, color: "#334155", marginBottom: "4px" }}>Cliente</div>
                    <DropDownListCustomized
                      key={`cliente-${selectorKey}`}
                      list={clientOptions}
                      placeholder="Selecciona un cliente"
                      required={false}
                      errorPosition="down"
                      defaultValue={selector.cliente.value}
                      onPermaValues={selector}
                      onPropertyChange="cliente"
                      onValueChange={handleSelectorChange}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: "12px", fontWeight: 600, color: "#334155", marginBottom: "4px" }}>
                      Área de negocio <span style={{ fontWeight: 400, color: "#94a3b8" }}>(opcional)</span>
                    </div>
                    <DropDownListCustomized
                      key={`area-${selectorKey}-${clientId}`}
                      list={areaOptions}
                      placeholder={clientId ? "Todas las áreas del cliente" : "Todas"}
                      required={false}
                      errorPosition="down"
                      defaultValue={selector.area.value}
                      onPermaValues={selector}
                      onPropertyChange="area"
                      onValueChange={handleSelectorChange}
                    />
                  </div>
                </div>
              )}
            </div>

            <div style={{ padding: "8px 22px 16px", overflowY: "auto", flex: 1 }}>
              {awaitingSelection ? (
                <div style={{ margin: "16px 0", padding: "28px 16px", borderRadius: "10px", border: "1px dashed #ddd6fe", background: "#faf5ff", textAlign: "center", color: "#6d28d9", fontSize: "13px" }}>
                  Elige un cliente (y, si quieres, un área de negocio) para ver y cambiar su configuración.
                </div>
              ) : (
                <div
                  style={{
                    margin: "12px 0 4px",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    border: `1px solid ${theme.border}`,
                    borderLeft: `4px solid ${theme.accent}`,
                    background: theme.soft,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{ padding: "1px 8px", borderRadius: "10px", background: theme.accent, color: "#fff", fontSize: "10.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      {isBase ? "Base" : "Específica"}
                    </span>
                    <span style={{ fontSize: "13.5px", fontWeight: 700, color: theme.text }}>Estás editando: {targetName}</span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#475569", marginTop: "4px", lineHeight: 1.4 }}>
                    {isBase
                      ? "Se aplica por defecto a todos los clientes y a las personas que no tienen un proceso asignado. Un cliente o área de negocio solo cambia lo que sea distinto."
                      : `Solo afecta a las postulaciones de ${scope === "AREA_NEGOCIO" ? "esta área" : "este cliente"}. Lo que no cambies se hereda de ${inheritsFrom}; los datos marcados "Propio" son la diferencia.`}
                  </div>
                  {!isBase && (
                    <button
                      type="button"
                      onClick={() => changeModoPantalla("BASE")}
                      style={{ marginTop: "6px", padding: 0, border: "none", background: "none", color: theme.accent, fontSize: "12px", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                    >
                      Volver a la configuración base
                    </button>
                  )}
                </div>
              )}

              {error && (
                <div role="alert" style={{ margin: "10px 0", padding: "10px 12px", borderRadius: "8px", background: "#fee2e2", color: "#b91c1c", fontSize: "13px" }}>{error}</div>
              )}
              {loading && (
                <div style={{ display: "flex", justifyContent: "center", padding: "32px" }}><CircularProgress size={26} /></div>
              )}
              {!loading && config && !awaitingSelection && sections.map(({ key, title, help, fields }) => (
                <section key={key} style={{ marginTop: "14px" }}>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {title}
                  </div>
                  <div style={{ fontSize: "11.5px", color: "#94a3b8", margin: "2px 0 4px" }}>{help}</div>
                  {fields.map((field) => {
                    const own = draft[field.key] ?? "HEREDA";
                    const isOwn = !isBase && own !== "HEREDA";
                    const changed = (config.own[field.key] ?? "HEREDA") !== own;
                    const checked = seValida(config, draft, field.key);
                    const inheritedSource = config.inherited[field.key]?.source ?? "DEFECTO";
                    return (
                      <div
                        key={field.key}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "3px 6px 3px 10px", borderRadius: "8px", background: changed ? theme.soft : "transparent" }}
                      >
                        <label htmlFor={`completitud-${field.key}`} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#1e293b", cursor: "pointer", flex: 1 }}>
                          {field.label}
                          {isOwn && (
                            <span style={{ padding: "1px 7px", borderRadius: "10px", background: "#ede9fe", color: "#6d28d9", fontSize: "10.5px", fontWeight: 700 }}>
                              Propio
                            </span>
                          )}
                        </label>
                        {isOwn && (
                          <button
                            type="button"
                            onClick={() => setDraft((prev) => ({ ...prev, [field.key]: "HEREDA" }))}
                            title={`Quita la regla propia y usa la de ${ORIGEN_LABEL[inheritedSource]}`}
                            style={{ border: "none", background: "none", color: "#64748b", fontSize: "11.5px", cursor: "pointer", padding: 0 }}
                          >
                            Usar la de {ORIGEN_LABEL[inheritedSource]}
                          </button>
                        )}
                        <span style={{ width: "62px", textAlign: "right", fontSize: "11.5px", fontWeight: 600, color: checked ? "#047857" : "#94a3b8" }}>
                          {checked ? "Se valida" : "No"}
                        </span>
                        <Switch
                          id={`completitud-${field.key}`}
                          checked={checked}
                          onChange={(event) => setDraft((prev) => alternarCampo(config, prev, field, event.target.checked))}
                          inputProps={{ "aria-label": `Validar ${field.label}` }}
                          size="small"
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: theme.accent },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: theme.accent },
                          }}
                        />
                      </div>
                    );
                  })}
                </section>
              ))}
            </div>

            <div style={{ padding: "12px 22px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
              <span style={{ fontSize: "12px", color: "#64748b" }}>
                {hasChanges
                  ? `${Object.keys(changes).length} cambio${Object.keys(changes).length === 1 ? "" : "s"} sin guardar`
                  : "Los porcentajes se recalculan solos en los minutos siguientes a guardar."}
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button type="button" onClick={handleClose} style={{ padding: "7px 16px", borderRadius: "8px", border: "1px solid #cbd5e1", background: "#fff", color: "#334155", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                  Cerrar
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                  style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 18px", borderRadius: "8px", border: 0, background: theme.accent, opacity: !hasChanges || saving ? 0.45 : 1, color: "#fff", fontSize: "13px", fontWeight: 600, cursor: !hasChanges || saving ? "not-allowed" : "pointer" }}
                >
                  {saving && <CircularProgress size={14} thickness={5} sx={{ color: "#fff" }} />}
                  {isBase ? "Guardar base" : scope === "AREA_NEGOCIO" ? "Guardar para esta área" : "Guardar para este cliente"}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConfiguracionCompletitudModal;
